import { useState, useCallback } from 'react'
import { get, post, put, del } from '../helper/network'
import API from '../helper/api'
import type {
  WebsiteConfig, WebsiteGenerateRequest, WebsiteGenerateJob,
  AnalyticsOverview, AnalyticsInsight, WebsiteTemplate, WebsiteSaveRequest,
  BlockType,
} from '../types'

const defaultBranding = {
  logoUrl: '',
  primaryColor: '#f97316',
  secondaryColor: '#1a1a1a',
  fontFamily: 'system-ui',
}

const defaultButler = {
  enabled: true,
  botName: 'AI管家',
  avatarUrl: '',
  welcomeMessage: '有什么可以帮您？',
  position: 'bottom-right' as const,
}

/** Serialize block props to JSON string for backend (which expects String type for props field) */
function serializeBlockProps(data: any): any {
  if (!data) return data
  const result = { ...data }
  if (result.props && typeof result.props === 'object') {
    result.props = JSON.stringify(result.props)
  }
  return result
}

/** Convert boolean visible to 1/0 for backend Integer fields */
function toBackendVisible(v: boolean | number | undefined): number | undefined {
  if (v === true) return 1
  if (v === false) return 0
  return v as number | undefined
}

/** Serialize all blocks' props in a WebsiteSaveRequest for backend */
function serializeSaveRequest(data: WebsiteSaveRequest): any {
  return {
    ...data,
    pages: data.pages.map(p => ({
      ...p,
      visible: toBackendVisible(p.visible),
      blocks: p.blocks.map(b => ({
        ...serializeBlockProps(b),
        visible: toBackendVisible(b.visible),
      })),
    })),
  }
}

/** Recursively replace {{companyName}} placeholders in string values */
function replacePlaceholders(obj: any, companyName: string): any {
  if (typeof obj === 'string') {
    return obj.replace(/\{\{companyName\}\}/g, companyName)
  }
  if (Array.isArray(obj)) {
    return obj.map(item => replacePlaceholders(item, companyName))
  }
  if (obj && typeof obj === 'object') {
    const result: any = {}
    for (const key of Object.keys(obj)) {
      result[key] = replacePlaceholders(obj[key], companyName)
    }
    return result
  }
  return obj
}

function normalizeWebsite(data: any): WebsiteConfig {
  if (!data) return data
  const companyName = data.companyName || ''
  return {
    ...data,
    id: String(data.id),
    branding: data.branding || defaultBranding,
    butler: data.butler || defaultButler,
    pages: (data.pages || []).map((p: any) => ({
      ...p,
      id: String(p.id),
      visible: p.visible !== 0 && p.visible !== false,
      blocks: (p.blocks || []).map((b: any) => {
        let props = typeof b.props === 'string' ? JSON.parse(b.props) : (b.props || {})
        props = replacePlaceholders(props, companyName)
        return {
          ...b,
          id: String(b.id),
          visible: b.visible !== 0 && b.visible !== false,
          props,
        }
      }),
    })),
    nav: (data.nav || []).map((n: any) => ({
      ...n,
      id: String(n.id),
      pageId: n.pageId != null ? String(n.pageId) : undefined,
    })),
  }
}

export function useWebsite() {
  const [website, setWebsite] = useState<WebsiteConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generateJob, setGenerateJob] = useState<WebsiteGenerateJob | null>(null)
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null)
  const [insights, setInsights] = useState<AnalyticsInsight[]>([])

  const fetchWebsite = useCallback(async (websiteId?: string) => {
    setLoading(true)
    try {
      const res: any = await get(API.GET_WEBSITE, websiteId ? { websiteId } : undefined)
      if (res?.code === 200 && res.data) {
        setWebsite(normalizeWebsite(res.data))
      }
    } catch {}
    setLoading(false)
  }, [])

  const fetchPublicWebsite = useCallback(async (shortcode: string) => {
    setLoading(true)
    try {
      const res: any = await get(API.GET_WEBSITE_PUBLIC, { shortcode })
      if (res?.code === 200 && res.data) {
        const normalized = normalizeWebsite(res.data)
        setWebsite(normalized)
        setLoading(false)
        return normalized
      }
    } catch {}
    setLoading(false)
    return null
  }, [])

  const generateWebsite = useCallback(async (request: WebsiteGenerateRequest) => {
    setGenerating(true)
    setGenerateJob(null)
    try {
      const res: any = await post(API.POST_WEBSITE_GENERATE, request)
      if (res?.code === 200 && res.data) {
        setGenerateJob({ jobId: res.data.jobId, status: 'QUEUED', progress: 0 })
        return res.data.jobId as string
      }
    } catch {}
    setGenerating(false)
    return null
  }, [])

  const pollGenerateStatus = useCallback(async (jobId: string): Promise<WebsiteGenerateJob | null> => {
    try {
      const res: any = await get(API.GET_WEBSITE_STATUS, { jobId })
      if (res?.code === 200 && res.data) {
        const job = res.data as WebsiteGenerateJob
        setGenerateJob(job)
        if (job.status === 'COMPLETED' || job.status === 'FAILED') {
          setGenerating(false)
        }
        return job
      }
    } catch {}
    return null
  }, [])

  const updateWebsite = useCallback(async (data: Partial<WebsiteConfig>) => {
    try {
      const res: any = await put(API.PUT_WEBSITE, data)
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const updateBlock = useCallback(async (blockId: string, data: { props?: Record<string, any>; visible?: boolean; blockOrder?: number }) => {
    try {
      const payload = serializeBlockProps({ blockId, ...data })
      if (payload.visible !== undefined) payload.visible = toBackendVisible(payload.visible)
      const res: any = await put(API.PUT_WEBSITE_BLOCK, payload)
      return res?.code === 200
    } catch {}
    return false
  }, [])

  const addPage = useCallback(async (data: { slug: string; title: string; titleEn?: string }) => {
    try {
      const res: any = await post(API.POST_WEBSITE_PAGE_ADD, data)
      if (res?.code === 200) {
        await fetchWebsite()
        return res.data?.pageId as string
      }
    } catch {}
    return null
  }, [fetchWebsite])

  const deletePage = useCallback(async (pageId: string) => {
    try {
      const res: any = await del(API.DELETE_WEBSITE_PAGE, { pageId })
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const reorderPages = useCallback(async (pageOrders: Array<{ pageId: string; order: number }>) => {
    try {
      const res: any = await put(API.PUT_WEBSITE_PAGE_REORDER, { pageOrders })
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const publishWebsite = useCallback(async () => {
    try {
      const res: any = await post(API.POST_WEBSITE_PUBLISH)
      if (res?.code === 200) {
        await fetchWebsite()
        return res.data as { url: string; publishedAt: string }
      }
    } catch {}
    return null
  }, [fetchWebsite])

  const unpublishWebsite = useCallback(async () => {
    try {
      const res: any = await post(API.POST_WEBSITE_UNPUBLISH)
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const updateButler = useCallback(async (data: { enabled?: boolean; botName?: string; avatarUrl?: string; welcomeMessage?: string; position?: string; appId?: string }) => {
    try {
      const res: any = await put(API.PUT_WEBSITE_BUTLER, data)
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const fetchAnalytics = useCallback(async (websiteId?: string, days = 30) => {
    try {
      const res: any = await get(API.GET_WEBSITE_ANALYTICS, { websiteId, days })
      if (res?.code === 200 && res.data) {
        setAnalytics(res.data)
        return res.data as AnalyticsOverview
      }
    } catch {}
    return null
  }, [])

  const fetchInsights = useCallback(async (websiteId?: string) => {
    try {
      const res: any = await get(API.GET_WEBSITE_AI_INSIGHTS, { websiteId })
      if (res?.code === 200 && res.data?.insights) {
        setInsights(res.data.insights)
        return res.data.insights as AnalyticsInsight[]
      }
    } catch {}
    return []
  }, [])

  // ---- Template ----
  const fetchTemplateList = useCallback(async (industryType?: string): Promise<WebsiteTemplate[]> => {
    try {
      const res: any = await get(API.GET_WEBSITE_TEMPLATE_LIST, industryType ? { industryType } : undefined)
      if (res?.code === 200 && res.data) {
        return Array.isArray(res.data) ? res.data : []
      }
    } catch {}
    return []
  }, [])

  const fetchTemplateDetail = useCallback(async (templateId: string): Promise<WebsiteTemplate | null> => {
    try {
      const res: any = await get(API.GET_WEBSITE_TEMPLATE_DETAIL + templateId)
      if (res?.code === 200 && res.data) {
        return res.data
      }
    } catch {}
    return null
  }, [])

  // ---- Bulk Save ----
  const saveWebsite = useCallback(async (data: WebsiteSaveRequest): Promise<boolean> => {
    setSaving(true)
    try {
      const res: any = await put(API.PUT_WEBSITE_SAVE, serializeSaveRequest(data))
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    setSaving(false)
    return false
  }, [fetchWebsite])

  // ---- Block CRUD ----
  const addBlock = useCallback(async (data: { pageId: string; blockType: BlockType; blockOrder?: number; props?: Record<string, any> }): Promise<boolean> => {
    try {
      const res: any = await post(API.POST_WEBSITE_BLOCK_ADD, serializeBlockProps(data))
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const deleteBlock = useCallback(async (blockId: string): Promise<boolean> => {
    try {
      const res: any = await del(API.DELETE_WEBSITE_BLOCK_DELETE, { blockId })
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  // ---- Nav CRUD ----
  const addNav = useCallback(async (data: { label: string; labelEn?: string; pageId?: string; externalUrl?: string; navOrder: number }): Promise<boolean> => {
    try {
      const res: any = await post(API.POST_WEBSITE_NAV_ADD, data)
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const updateNav = useCallback(async (data: { id: string; label?: string; labelEn?: string; pageId?: string; externalUrl?: string; navOrder?: number }): Promise<boolean> => {
    try {
      const res: any = await put(API.PUT_WEBSITE_NAV_UPDATE, data)
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  const deleteNav = useCallback(async (navId: string): Promise<boolean> => {
    try {
      const res: any = await del(API.DELETE_WEBSITE_NAV_DELETE, { navId })
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  // ---- Branding ----
  const updateBranding = useCallback(async (data: Partial<WebsiteConfig['branding']>): Promise<boolean> => {
    try {
      const res: any = await put(API.PUT_WEBSITE_BRANDING_UPDATE, data)
      if (res?.code === 200) {
        await fetchWebsite()
        return true
      }
    } catch {}
    return false
  }, [fetchWebsite])

  return {
    website,
    setWebsite,
    loading,
    saving,
    generating,
    generateJob,
    analytics,
    insights,
    fetchWebsite,
    fetchPublicWebsite,
    generateWebsite,
    pollGenerateStatus,
    updateWebsite,
    updateBlock,
    addBlock,
    deleteBlock,
    addPage,
    deletePage,
    reorderPages,
    addNav,
    updateNav,
    deleteNav,
    updateBranding,
    publishWebsite,
    unpublishWebsite,
    updateButler,
    fetchAnalytics,
    fetchInsights,
    fetchTemplateList,
    fetchTemplateDetail,
    saveWebsite,
  }
}
